// @ts-check
/** @typedef {import('../types/api').Task} Task */
/** @typedef {import('../types/api').AdminUser} AdminUser */

import {
  closestCorners,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import SortableTaskCard from '../components/tasks/SortableTaskCard';
import TaskCard from '../components/tasks/TaskCard';
import TaskCreateModal from '../components/tasks/TaskCreateModal';
import TaskDetailModal from '../components/tasks/TaskDetailModal';
import taskService from '../services/taskService';
import api from '../utils/apiClient';

const COLUMNS = [
  { id: 'to_do', label: 'To Do', color: 'bg-muted' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-blue-100 dark:bg-blue-900/40' },
  { id: 'review', label: 'Review', color: 'bg-purple-100 dark:bg-purple-900/40' },
  { id: 'done', label: 'Done', color: 'bg-green-100 dark:bg-green-900/40' },
  { id: 'blocked', label: 'Blocked', color: 'bg-red-100 dark:bg-red-900/40' },
];

const TaskKanbanPage = () => {
  /** @type {[Record<string, Task[]>, Function]} */
  const [tasksByStatus, setTasksByStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    // Touch: press and hold to pick a card up, so a normal swipe still scrolls the board.
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 8,
      },
    }),
  );

  useEffect(() => {
    const userData = localStorage.getItem('adminUser');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }

    loadTasks();
    loadAdminUsers();
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = await taskService.getTasks({});

      // Group tasks by status
      const grouped = COLUMNS.reduce((acc, col) => {
        acc[col.id] = data.filter((task) => task.status === col.id);
        return acc;
      }, {});

      setTasksByStatus(grouped);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminUsers = async () => {
    try {
      const data = await api.get('/api/admin/users?limit=200');
      setAdminUsers(data.items);
    } catch (error) {
      console.error('Error loading admin users:', error);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    const taskId = active.id;
    const newStatus = over.id;

    // Find the task being dragged
    const task = Object.values(tasksByStatus)
      .flat()
      .find((t) => t.id === taskId);

    if (!task) {
      setActiveId(null);
      return;
    }

    // If status changed, update on backend
    if (task.status !== newStatus) {
      try {
        // Optimistic update
        const oldStatus = task.status;
        const updatedTasksByStatus = { ...tasksByStatus };

        // Remove from old column
        updatedTasksByStatus[oldStatus] = updatedTasksByStatus[oldStatus].filter((t) => t.id !== taskId);

        // Add to new column with updated status
        const updatedTask = { ...task, status: newStatus };
        updatedTasksByStatus[newStatus] = [...updatedTasksByStatus[newStatus], updatedTask];

        setTasksByStatus(updatedTasksByStatus);

        // Update on backend
        await taskService.updateTaskStatus(taskId, newStatus);
      } catch (error) {
        console.error('Error updating task status:', error);
        // Reload tasks on error to revert optimistic update
        loadTasks();
      }
    }

    setActiveId(null);
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
  };

  const handleTaskUpdate = async () => {
    await loadTasks();
    if (selectedTask) {
      const updated = await taskService.getTask(selectedTask.id);
      setSelectedTask(updated);
    }
  };

  const activeTask = activeId
    ? Object.values(tasksByStatus)
        .flat()
        .find((task) => task.id === activeId)
    : null;

  return (
    <div className="bg-muted/50">
      {/* Header - Fixed at top */}
      <div className="sticky top-0 z-20 bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Kanban Board</h1>
            <p className="mt-2 text-sm text-muted-foreground">Drag tasks between columns to update status</p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
          >
            <Plus size={20} />
            Create Task
          </button>
        </div>
      </div>

      {/* Kanban Board - Scrollable Container */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <LoadingScreen />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="overflow-x-auto pb-4">
              <div className="flex gap-4 min-w-max">
                {COLUMNS.map((column) => {
                  const tasks = tasksByStatus[column.id] || [];

                  return (
                    <SortableContext
                      key={column.id}
                      id={column.id}
                      items={tasks.map((task) => task.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="flex-shrink-0 w-80">
                        {/* Column Header */}
                        <div className={`${column.color} rounded-t-lg px-4 py-3 border-b-2 border-input`}>
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <h3 className="font-semibold text-foreground">{column.label}</h3>
                            <span className="bg-card text-foreground/80 text-xs font-medium px-2 py-1 rounded-full">
                              {tasks.length}
                            </span>
                          </div>
                        </div>

                        {/* Column Body - Droppable Area */}
                        <div className="bg-card border border-t-0 border-border rounded-b-lg p-3 min-h-[500px] space-y-3">
                          {tasks.length === 0 ? (
                            <p className="text-center text-muted-foreground/70 text-sm py-8">No tasks</p>
                          ) : (
                            tasks.map((task) => (
                              <SortableTaskCard key={task.id} task={task} onClick={handleTaskClick} />
                            ))
                          )}
                        </div>
                      </div>
                    </SortableContext>
                  );
                })}
              </div>
            </div>

            {/* Drag Overlay */}
            <DragOverlay>
              {activeTask ? (
                <div className="opacity-80 rotate-3 transform scale-105">
                  <TaskCard task={activeTask} />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Modals */}
      <TaskCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={loadTasks}
        adminUsers={adminUsers}
        currentUser={currentUser}
      />

      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleTaskUpdate}
          adminUsers={adminUsers}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default TaskKanbanPage;
